import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const STORAGE_KEY = 'enklespill:progress:v1';

export interface LevelCompletion {
  count: number;
  lastPlayed: string;
}

export interface ProgressState {
  completed: Record<string, LevelCompletion>;
  stickers: string[];
  unlocks: string[];
  muted: boolean;
  markCompleted: (levelId: string, sticker?: string, unlock?: string) => void;
  addSticker: (stickerId: string) => void;
  addUnlock: (unlockId: string) => void;
  setMuted: (muted: boolean) => void;
  isCompleted: (levelId: string) => boolean;
  hasSticker: (stickerId: string) => boolean;
}

type PersistedProgress = Pick<ProgressState, 'completed' | 'stickers' | 'unlocks' | 'muted'>;

function migrate(persisted: unknown): PersistedProgress {
  if (!persisted || typeof persisted !== 'object') {
    return { completed: {}, stickers: [], unlocks: [], muted: false };
  }
  const raw = persisted as Partial<PersistedProgress>;
  return {
    completed: raw.completed ?? {},
    stickers: Array.isArray(raw.stickers) ? raw.stickers : [],
    unlocks: Array.isArray(raw.unlocks) ? raw.unlocks : [],
    muted: Boolean(raw.muted),
  };
}

export const useProgressStore = create<ProgressState>()(
  persist(
    (set, get) => ({
      completed: {},
      stickers: [],
      unlocks: [],
      muted: false,

      markCompleted: (levelId, sticker, unlock) => {
        const now = new Date().toISOString();
        set((state) => {
          const prev = state.completed[levelId];
          const stickers =
            sticker && !state.stickers.includes(sticker)
              ? [...state.stickers, sticker]
              : state.stickers;
          const unlocks =
            unlock && !state.unlocks.includes(unlock)
              ? [...state.unlocks, unlock]
              : state.unlocks;
          return {
            completed: {
              ...state.completed,
              [levelId]: {
                count: (prev?.count ?? 0) + 1,
                lastPlayed: now,
              },
            },
            stickers,
            unlocks,
          };
        });
      },

      addSticker: (stickerId) => {
        set((state) =>
          state.stickers.includes(stickerId)
            ? state
            : { stickers: [...state.stickers, stickerId] }
        );
      },

      addUnlock: (unlockId) => {
        set((state) =>
          state.unlocks.includes(unlockId)
            ? state
            : { unlocks: [...state.unlocks, unlockId] }
        );
      },

      setMuted: (muted) => set({ muted }),

      isCompleted: (levelId) => Boolean(get().completed[levelId]),

      hasSticker: (stickerId) => get().stickers.includes(stickerId),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      migrate: (persisted) => migrate(persisted),
      partialize: (state) => ({
        completed: state.completed,
        stickers: state.stickers,
        unlocks: state.unlocks,
        muted: state.muted,
      }),
    }
  )
);
